import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ServiceRate,
  ServiceRatesListResponse,
  TimeEntriesListResponse,
  TimeEntry,
} from '@stfors/shared-types';
import { api } from '../client';

// The service-rates API returns rows ranked most-specific-first (project →
// client → global), so the first row for each name is the effective one.
// Collapse to one entry per name — the rate that actually applies/bills.
export function effectiveRates(rows: ServiceRate[]): ServiceRate[] {
  const seen = new Set<string>();
  const out: ServiceRate[] = [];
  for (const r of rows) {
    if (seen.has(r.name)) continue;
    seen.add(r.name);
    out.push(r);
  }
  return out;
}

// Rates for the time-entry picker. With a projectId, the API returns the
// project's EFFECTIVE rate sheet — global + the project's client overrides +
// the project's own overrides, ranked most-specific-first (Project → Client →
// Global) — so the picker can collapse to one effective rate per name. With no
// arg it returns the full catalog.
export function useServiceRates(projectId?: number) {
  const qs = projectId !== undefined ? `?project_id=${projectId}` : '';
  return useQuery({
    queryKey: ['service-rates', { project_id: projectId ?? null }],
    queryFn: ({ signal }) =>
      api.get<ServiceRatesListResponse>(`/api/service-rates${qs}`, signal),
  });
}

export function useTimeEntries(projectId?: number) {
  const qs = projectId !== undefined ? `?project_id=${projectId}` : '';
  return useQuery({
    queryKey: ['time-entries', { project_id: projectId ?? null }],
    queryFn: ({ signal }) =>
      api.get<TimeEntriesListResponse>(`/api/time-entries${qs}`, signal),
  });
}

// Writing a time entry moves more than the list: the create/edit paths debit
// or rebalance the project's retainer, unbilled hours feed the dashboard
// KPIs and the project detail page, and an entry that sells a drive marks it
// Reserved/Sold on the Assets page (0058). Invalidate them together.
function invalidateTimeEntryReaders(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['time-entries'] });
  void qc.invalidateQueries({ queryKey: ['retainers'] });
  void qc.invalidateQueries({ queryKey: ['kpi'] });
  void qc.invalidateQueries({ queryKey: ['projects'] });
  void qc.invalidateQueries({ queryKey: ['assets'] });
  // Each matter's unbilled count (0061).
  void qc.invalidateQueries({ queryKey: ['project-matters'] });
}

export interface CreateTimeEntryInput {
  project_id: number;
  service_rate_id: number;
  /** Required for an hourly rate; optional (null) for a flat fee (0057). */
  hours: string | null;
  /** This entry's fee — flat-fee rates only; the API refuses it on hourly. */
  flat_amount?: string | null;
  /** The drive this flat-fee charge sells (0058). */
  asset_id?: number | null;
  /** The project's matter this work belongs to (0061). */
  matter_id?: number | null;
  date: number;
  description: string;
  /** Optional measured span. Both or neither — the DB CHECK requires it. */
  started_at_epoch?: number;
  ended_at_epoch?: number;
}

export function useCreateTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTimeEntryInput) =>
      api.post<{ time_entry: TimeEntry }>('/api/time-entries', input),
    onSuccess: () => invalidateTimeEntryReaders(qc),
  });
}

export interface UpdateTimeEntryInput {
  id: number;
  project_id?: number;
  /** Reassignment — the API requires time_entry.approve for this field. */
  user_id?: number;
  service_rate_id?: number;
  hours?: string | null;
  flat_amount?: string | null;
  asset_id?: number | null;
  matter_id?: number | null;
  date?: number;
  description?: string;
  started_at_epoch?: number | null;
  ended_at_epoch?: number | null;
}

export function useUpdateTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateTimeEntryInput) =>
      api.patch<{ time_entry: TimeEntry }>(`/api/time-entries/${id}`, patch),
    onSuccess: () => invalidateTimeEntryReaders(qc),
  });
}

export function useDeleteTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/time-entries/${id}`),
    onSuccess: () => invalidateTimeEntryReaders(qc),
  });
}

export function useApproveTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, approve }: { id: number; approve: boolean }) =>
      api.post<{ time_entry: TimeEntry }>(
        `/api/time-entries/${id}/${approve ? 'approve' : 'unapprove'}`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['time-entries'] });
    },
  });
}
